
import Vue from 'vue';
import gql from 'graphql-tag';
import { IGroup } from '../../store/advertiser-insights';
import Tooltip from '~shared/components/generic/Tooltip.vue';
import { normalizeDateRange } from '~shared/utils';
import QueryParamButtonGroup from '~shared/components/selectors/QueryParamButtonGroup.vue';

interface IField {
  key: string;
  label: string;
  sortable?: boolean;
  formatter?: Function;
}

export default Vue.extend({
  components: {
    QueryParamButtonGroup,
    Tooltip,
  },
  props: {
    includeArchivedCampaigns: {
      type: Boolean,
      default: false,
    },
    isNoData: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      startDate: '',
      endDate: '',
      stats: [],
      orderBy: 'CLICKS',
      orderDirection: 'DESC',
      sortBy: 'clicks',
      sortDesc: true,
      perPage: 25,
      currentPage: 1,
      domainOrSiteColName: 'SITE',
      domainColumnOptions: [
        { label: 'Site Name', value: 'SITE' },
        { label: 'Domain', value: 'DOMAIN' },
      ],
      loading: false,
    };
  },
  computed: {
    fields(): IField[] {
      if (!this.groupId) {
        return [];
      }
      let domain = this.selectedGroup.reportSettings.domain;
      if (!domain || domain?.length === 0) {
        domain = ['domain', 'clicks', 'impressions', 'ctr'];
      }
      const res: IField[] = [];

      if (domain.includes('domain')) {
        res.push({ key: 'domain', label: 'Domain' });
      }

      if (domain.includes('impressions')) {
        res.push({
          key: 'impressions',
          label: 'Impressions',
          sortable: true,
        });
      }

      if (domain.includes('clicks')) {
        res.push({
          key: 'clicks',
          label: 'Clicks',
          sortable: true,
        });
      }

      if (domain.includes('ctr')) {
        res.push({
          key: 'ctr',
          label: 'CTR',
          sortable: true,
          formatter(item) {
            return `${
              item === null || item === undefined || isNaN(item)
                ? 0
                : (+item).toFixed(2)
            }%`;
          },
        });
      }

      return res;
    },
    selectedGroup(): IGroup | null {
      const group =
        this.$store.state['advertiser-insights'].filters.selectedGroup;
      if (!group || !group.id) {
        return null;
      }
      return group;
    },
    groupId(): string | null {
      return this.selectedGroup ? this.selectedGroup.id : null;
    },
    campaignId(): string | null {
      const selectedCampaign =
        this.$store.state['advertiser-insights'].filters.selectedCampaign;
      if (!selectedCampaign || !selectedCampaign.id) {
        return null;
      }
      return selectedCampaign.id;
    },
    selectedDateRange(): Date[] {
      return this.$store.state['advertiser-insights'].filters.selectedDateRange;
    },
    rows(): any {
      return this.stats.length;
    },
  },
  watch: {
    selectedDateRange: {
      handler(val): void {
        if (val.length === 2) {
          const { startDate, endDate } = normalizeDateRange({
            startDate: val[0],
            endDate: val[1],
          });

          this.startDate = startDate.toISOString();
          this.endDate = endDate.toISOString();
        }
      },
      deep: true,
      immediate: true,
    },
    stats: {
      handler(val): void {
        this.$emit('update:isNoData', this.loading || !val.length);
      },
      deep: true,
      immediate: true,
    },
    loading: {
      handler(val): void {
        this.$emit('update:isNoData', val || !this.stats.length);
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    sortChanged(val) {
      this.orderBy = val.sortBy.toUpperCase();
      if (val.sortDesc) {
        this.orderDirection = 'DESC';
      } else {
        this.orderDirection = 'ASC';
      }
    },
    setDomainOrSiteColName(val) {
      this.domainOrSiteColName = val;
    },
  },
  apollo: {
    stats: {
      query: gql`
        query (
          $groupId: ID!
          $campaignId: ID!
          $startDate: DateTime!
          $endDate: DateTime!
          $includeArchivedCampaigns: Boolean
          $orderBy: DomainReportOrderBy
          $orderDirection: OrderDirection
          $limit: Int
          $reportBy: String
        ) {
          CampaignDomainStatistics(
            groupId: $groupId
            campaignId: $campaignId
            startDate: $startDate
            endDate: $endDate
            includeArchivedCampaigns: $includeArchivedCampaigns
            orderBy: $orderBy
            orderDirection: $orderDirection
            limit: $limit
            reportBy: $reportBy
          ) {
            statistics {
              clicks
              impressions
              ctr
              domain
            }
          }
        }
      `,
      fetchPolicy: 'no-cache',
      variables(): any {
        return {
          campaignId: this.campaignId || '',
          startDate: this.startDate,
          endDate: this.endDate,
          groupId: this.groupId,
          includeArchivedCampaigns: this.includeArchivedCampaigns,
          orderBy: this.orderBy,
          orderDirection: this.orderDirection,
          limit: 100,
          reportBy: this.domainOrSiteColName,
        };
      },
      watchLoading(isLoading) {
        this.loading = isLoading;
      },
      skip(): boolean {
        return !this.groupId || !this.startDate || !this.endDate;
      },
      notifyOnNetworkStatusChange: true,
      debounce: 200,
      update(data): [] {
        if (data.CampaignDomainStatistics) {
          return data.CampaignDomainStatistics.statistics;
        }
        return [];
      },
    },
  },
});
